import React, { useState, useEffect, useMemo } from "react";
import { ArrowBack, Assessment, CalendarToday, TrendingUp, Receipt } from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { Chart } from "react-google-charts";
import fetchClient from "../../api/fetchClient";
import Loader from "../../components/Loader";
import message from "../../helper/message";
import { formatAbbrValue } from "../../helper/formatValue";
import {
    Container,
    ModernHeader,
    HeaderLeft,
    HeaderContent,
    PageTitle,
    PageSubtitle,
    BackButton,
    ResultsCard,
    getTheme,
} from "../../components/common";

function DashboardViceri() {
    const history = useHistory();
    const theme = getTheme('viceri');
    const [isLoading, setIsLoading] = useState(false);
    const [metrics, setMetrics] = useState([]);
    const [aplicacoes, setAplicacoes] = useState([]);

    // Filtros
    const currentYear = new Date().getFullYear();
    const [selectedApp, setSelectedApp] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [availableYears, setAvailableYears] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [typeChartMode, setTypeChartMode] = useState('count');
    const [selectedMonth, setSelectedMonth] = useState("");

    useEffect(() => {
        let isMounted = true;

        const fetchAplicacoes = async () => {
            try {
                const { data } = await fetchClient.get("/viceri/aplicacao/getAllAplicacao");
                if (isMounted) {
                    setAplicacoes(data);
                }
            } catch (error) {
                if (isMounted) {
                    console.error(error);
                    message.error("Erro ao buscar aplicações");
                }
            }
        };

        fetchAplicacoes();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        const extractYears = (data) => {
            const years = new Set();
            Object.keys(data).forEach(monthKey => {
                const [year] = monthKey.split('-');
                years.add(year);
            });
            return Array.from(years).sort((a, b) => b - a); // Ordenar decrescente
        };

        const processData = (data, app, yearFilter) => {
            const metricsArray = [];

            Object.keys(data).forEach(monthKey => {
                const monthData = data[monthKey];
                let totalProcessed = 0;
                let totalValue = 0;

                ['purchase', 'return', 'downpayment'].forEach(type => {
                    if (monthData[type]) {
                        totalProcessed += monthData[type].processed || 0;
                        totalValue += monthData[type].totalValue || 0;
                    }
                });

                // Extrair mês do formato YYYY-MM-DD
                const [year, month] = monthKey.split('-');

                // Filtrar pelo ano selecionado (se houver filtro)
                if (!yearFilter || year === yearFilter) {
                    metricsArray.push({
                        month: parseInt(month),
                        year: parseInt(year),
                        transactions: totalProcessed,
                        totalValue: totalValue,
                        appId: app?.id || 'all',
                        appName: app?.description || 'Todas',
                        purchase: monthData.purchase || { processed: 0, totalValue: 0 },
                        return: monthData.return || { processed: 0, totalValue: 0 },
                        downpayment: monthData.downpayment || { processed: 0, totalValue: 0 },
                        provisioned: app?.provisioned || 0
                    });
                }
            });

            return metricsArray;
        };

        const fetchMetrics = async () => {
            if (!selectedApp) return;

            try {
                if (isMounted) {
                    setIsLoading(true);
                }

                let allMetrics = [];
                let allYears = new Set();

                if (selectedApp === 'all') {
                    // Buscar dados de todas as aplicações
                    for (const app of aplicacoes) {
                        try {
                            const { data } = await fetchClient.get(`/viceri/dashboard/${app.id}`);
                            // Extrair anos disponíveis
                            extractYears(data).forEach(y => allYears.add(y));
                            const appMetrics = processData(data, app, selectedYear);
                            allMetrics = [...allMetrics, ...appMetrics];
                        } catch (err) {
                            console.log(`Erro ao buscar dados da aplicação ${app.id}:`, err);
                        }
                    }
                } else {
                    const { data } = await fetchClient.get(`/viceri/dashboard/${selectedApp}`);
                    const app = aplicacoes.find(a => a.id === selectedApp);
                    // Extrair anos disponíveis
                    extractYears(data).forEach(y => allYears.add(y));
                    allMetrics = processData(data, app, selectedYear);
                }

                if (isMounted) {
                    const yearsArray = Array.from(allYears).sort((a, b) => b - a);
                    setAvailableYears(yearsArray);

                    // Se não tem ano selecionado, seleciona o mais recente
                    if (!selectedYear && yearsArray.length > 0) {
                        setSelectedYear(yearsArray[0]);
                    }

                    setMetrics(allMetrics);
                }
            } catch (error) {
                if (isMounted) {
                    console.error(error);
                    message.error(error.response?.data?.message || "Erro ao buscar métricas");
                    setMetrics([]);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        if (selectedApp && aplicacoes.length > 0) {
            fetchMetrics();
        }

        return () => {
            isMounted = false;
        };
    }, [selectedApp, selectedYear, aplicacoes]);

    // Preparar dados para o gráfico de evolução de transações
    const prepareEvolutionChartData = () => {
        if (!filteredMetrics || filteredMetrics.length === 0) {
            return [["Mês", "Transações"]];
        }

        const monthNames = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

        // Agregar dados por mês (para caso de "all")
        const monthlyData = {};

        filteredMetrics.forEach(metric => {
            const month = metric.month;
            if (!monthlyData[month]) {
                monthlyData[month] = 0;
            }
            monthlyData[month] += metric.transactions;
        });

        const chartData = [["Mês", "Transações"]];

        Object.keys(monthlyData)
            .map(m => parseInt(m))
            .sort((a, b) => a - b)
            .forEach(month => {
                const monthIndex = month - 1;
                const monthName = monthNames[monthIndex];
                chartData.push([monthName, monthlyData[month]]);
            });

        return chartData;
    };

    // Preparar dados para o gráfico por tipo de documento
    const prepareTypeChartData = () => {
        if (!filteredMetrics || filteredMetrics.length === 0) {
            return [["Tipo", "Quantidade"]];
        }

        let totalPurchase = 0;
        let totalReturn = 0;
        let totalDownpayment = 0;

        filteredMetrics.forEach(metric => {
            totalPurchase += metric.purchase?.processed || 0;
            totalReturn += metric.return?.processed || 0;
            totalDownpayment += metric.downpayment?.processed || 0;
        });

        return [
            ["Tipo", "Quantidade"],
            ["Compras", totalPurchase],
            ["Devoluções", totalReturn],
            ["Adiantamentos", totalDownpayment]
        ];
    };

    // Preparar dados para o gráfico por tipo - valor
    const prepareTypeValueData = () => {
        if (!filteredMetrics || filteredMetrics.length === 0) {
            return [["Tipo", "Valor (R$)"]];
        }
        let totalPurchase = 0;
        let totalReturn = 0;
        let totalDownpayment = 0;
        filteredMetrics.forEach(metric => {
            totalPurchase += metric.purchase?.totalValue || 0;
            totalReturn += metric.return?.totalValue || 0;
            totalDownpayment += metric.downpayment?.totalValue || 0;
        });
        return [
            ["Tipo", "Valor (R$)"],
            ["Compras", totalPurchase],
            ["Devoluções", totalReturn],
            ["Adiantamentos", totalDownpayment]
        ];
    };

    // Preparar dados para o gráfico por aplicação (quando "all" está selecionado)
    const prepareAppChartData = () => {
        if (!filteredMetrics || filteredMetrics.length === 0 || selectedApp !== 'all') {
            return [["Aplicação", "Processos"]];
        }

        // Agrupar dados por aplicação
        const appData = {};

        filteredMetrics.forEach(metric => {
            const appName = metric.appName;
            if (!appData[appName]) {
                appData[appName] = 0;
            }
            appData[appName] += metric.transactions || 0;
        });

        const chartData = [["Aplicação", "Processos"]];

        Object.entries(appData)
            .sort((a, b) => b[1] - a[1])
            .forEach(([appName, transactions]) => {
                chartData.push([appName, transactions]);
            });

        return chartData;
    };

    // Preparar dados para o gráfico de valores por tipo
    const prepareValueChartData = () => {
        if (!filteredMetrics || filteredMetrics.length === 0) {
            return [["Mês", "Compras", "Devoluções", "Adiantamentos"]];
        }

        const monthNames = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

        // Agregar dados por mês (para caso de "all")
        const monthlyData = {};

        filteredMetrics.forEach(metric => {
            const month = metric.month;
            if (!monthlyData[month]) {
                monthlyData[month] = {
                    purchase: 0,
                    return: 0,
                    downpayment: 0
                };
            }
            monthlyData[month].purchase += metric.purchase?.totalValue || 0;
            monthlyData[month].return += metric.return?.totalValue || 0;
            monthlyData[month].downpayment += metric.downpayment?.totalValue || 0;
        });

        const chartData = [["Mês", "Compras", "Devoluções", "Adiantamentos"]];

        Object.keys(monthlyData)
            .map(m => parseInt(m))
            .sort((a, b) => a - b)
            .forEach(month => {
                const monthIndex = month - 1;
                const monthName = monthNames[monthIndex];
                chartData.push([
                    monthName,
                    monthlyData[month].purchase,
                    monthlyData[month].return,
                    monthlyData[month].downpayment
                ]);
            });

        return chartData;
    };

    // Calcular totalizadores
    const calculateTotals = () => {
        if (!filteredMetrics || filteredMetrics.length === 0) {
            return {
                transactions: 0,
                totalValue: 0,
                averagePerMonth: 0,
                provisioned: 0
            };
        }

        const transactions = filteredMetrics.reduce((sum, m) => sum + (m.transactions || 0), 0);
        const totalValue = filteredMetrics.reduce((sum, m) => sum + (m.totalValue || 0), 0);
        const monthsWithData = filteredMetrics.length;
        const provisioned = (filteredMetrics[0]?.provisioned || 0) * monthsWithData;

        return {
            transactions,
            totalValue,
            averagePerMonth: monthsWithData > 0 ? Math.round(transactions / monthsWithData) : 0,
            provisioned
        };
    };

    const filteredMetrics = useMemo(() => {
        if (!selectedMonth) return metrics;
        return metrics.filter(m => m.month === parseInt(selectedMonth));
    }, [metrics, selectedMonth]);

    const kpiVariation = useMemo(() => {
        const pct = (curr, prev) => (prev > 0 ? ((curr - prev) / prev) * 100 : null);
        if (!metrics.length) return { transactions: null, value: null };
        const grouped = {};
        metrics.forEach((m) => {
            const mo = parseInt(m.month);
            if (!grouped[mo]) grouped[mo] = { transactions: 0, totalValue: 0 };
            grouped[mo].transactions += m.transactions || 0;
            grouped[mo].totalValue += m.totalValue || 0;
        });
        const months = Object.keys(grouped).map(Number).sort((a, b) => a - b);
        if (months.length < 2) return { transactions: null, value: null };
        const last = grouped[months[months.length - 1]];
        const prev = grouped[months[months.length - 2]];
        return {
            transactions: pct(last.transactions, prev.transactions),
            value: pct(last.totalValue, prev.totalValue),
        };
    }, [metrics]);

    const sortedMetrics = useMemo(() => {
        return [...filteredMetrics].sort((a, b) => {
            if (selectedApp === 'all') {
                if (a.appName !== b.appName) {
                    return a.appName.localeCompare(b.appName);
                }
            }
            if (a.year !== b.year) return a.year - b.year;
            return a.month - b.month;
        });
    }, [filteredMetrics, selectedApp]);

    const totalPages = Math.max(1, Math.ceil(sortedMetrics.length / rowsPerPage));
    const paginatedMetrics = sortedMetrics.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [metrics, rowsPerPage, selectedMonth]);

    const totals = calculateTotals();
    const selectedAppName = selectedApp === 'all'
        ? 'Todas as aplicações'
        : aplicacoes.find(a => a.id === selectedApp)?.description || '';

    const evolutionChartOptions = {
        title: `Evolução Mensal de Processos${selectedYear ? ` - ${selectedYear}` : ''}`,
        chartArea: { width: "80%", height: "70%" },
        hAxis: {
            title: "Mês",
            titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
        },
        vAxis: {
            title: "Quantidade de Processos",
            titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
            minValue: 0,
        },
        colors: ["#667eea"],
        legend: { position: "none" },
        animation: {
            startup: true,
            duration: 1000,
            easing: "out",
        },
    };

    const typeChartOptions = {
        title: `Processos por Tipo${selectedYear ? ` - ${selectedYear}` : ''}`,
        pieHole: 0.4,
        colors: ["#667eea", "#764ba2", "#3498db"],
        legend: { position: "bottom" },
        animation: {
            startup: true,
            duration: 1000,
            easing: "out",
        },
    };

    const valueChartOptions = {
        title: `Valor por Tipo de Documento${selectedYear ? ` - ${selectedYear}` : ''}`,
        chartArea: { width: "75%", height: "70%" },
        hAxis: {
            title: "Mês",
            titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
        },
        vAxis: {
            title: "Valor (R$)",
            titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
            minValue: 0,
            format: "decimal",
        },
        colors: ["#667eea", "#764ba2", "#3498db"],
        legend: { position: "top" },
        isStacked: true,
        animation: {
            startup: true,
            duration: 1000,
            easing: "out",
        },
    };

    const exportCSV = () => {
        const BOM = "\uFEFF";
        const monthNames = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
                            "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
        const hasAppCol = selectedApp === 'all';
        const headers = (hasAppCol ? "Aplicação;" : "") + "Mês;Compras;Devoluções;Adiantamentos;Total Processos;Valor Total";
        const rows = sortedMetrics.map(metric => {
            const monthIndex = metric.month - 1;
            const monthName = monthNames[monthIndex];
            const cols = [
                ...(hasAppCol ? [metric.appName] : []),
                monthName,
                (metric.purchase?.processed || 0).toString(),
                (metric.return?.processed || 0).toString(),
                (metric.downpayment?.processed || 0).toString(),
                (metric.transactions || 0).toString(),
                (metric.totalValue || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
            ];
            return cols.join(";");
        });
        const csv = BOM + headers + "\n" + rows.join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `viceri-dashboard${selectedYear ? '-' + selectedYear : ''}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (isLoading && !metrics.length) return <Loader />;

    return (
        <Container>
            <ModernHeader>
                <HeaderLeft>
                    <BackButton color={theme.primary} onClick={() => history.goBack()}>
                        <ArrowBack />
                    </BackButton>
                    <HeaderContent>
                        <PageTitle>
                            <Assessment style={{ marginRight: '12px', fontSize: '32px' }} />
                            Dashboard Viceri
                        </PageTitle>
                        <PageSubtitle>Visualize métricas de processos por período</PageSubtitle>
                    </HeaderContent>
                </HeaderLeft>
            </ModernHeader>

            <ResultsCard>
                <FiltersCard>
                    <FilterTitle>Filtros</FilterTitle>
                    <FiltersContainer>
                        <FilterGroup>
                            <FilterLabel>Aplicação</FilterLabel>
                            <FilterSelect
                                value={selectedApp}
                                onChange={(e) => {
                                    setSelectedApp(e.target.value);
                                    setSelectedYear(''); // Reset ano para buscar anos disponíveis
                                    setAvailableYears([]);
                                    setSelectedMonth('');
                                }}
                            >
                                <option value="">Selecione uma aplicação</option>
                                <option value="all">Todas as aplicações</option>
                                {aplicacoes.map(app => (
                                    <option key={app.id} value={app.id}>
                                        {app.description}
                                    </option>
                                ))}
                            </FilterSelect>
                        </FilterGroup>

                        {selectedApp && (
                            <FilterGroup>
                                <FilterLabel>Ano</FilterLabel>
                                <FilterSelect
                                    value={selectedYear}
                                    onChange={(e) => { setSelectedYear(e.target.value); setSelectedMonth(''); }}
                                    disabled={availableYears.length === 0 || isLoading}
                                >
                                    {isLoading ? (
                                        <option value="">Carregando...</option>
                                    ) : availableYears.length === 0 ? (
                                        <option value="">Sem dados</option>
                                    ) : (
                                        availableYears.map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))
                                    )}
                                </FilterSelect>
                            </FilterGroup>
                        )}

                        {selectedApp && selectedYear && (
                            <FilterGroup>
                                <FilterLabel>Mês</FilterLabel>
                                <FilterSelect
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    {["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"].map((m, i) => {
                                        if (parseInt(selectedYear) === currentYear && i + 1 > new Date().getMonth() + 1) return null;
                                        return <option key={i + 1} value={i + 1}>{m}</option>;
                                    })}
                                </FilterSelect>
                            </FilterGroup>
                        )}
                    </FiltersContainer>
                </FiltersCard>

                {selectedApp && (
                    <>
                        <TotalizersGrid>
                            <TotalizerCard color="#667eea">
                                <TotalizerIcon>
                                    <Receipt />
                                </TotalizerIcon>
                                <TotalizerContent>
                                    <TotalizerValue>{formatAbbrValue(totals.transactions)}</TotalizerValue>
                                    <TotalizerLabel>Total de Processos</TotalizerLabel>
                                    {!selectedMonth && kpiVariation.transactions !== null && (
                                        <VariationBadge positive={kpiVariation.transactions >= 0}>
                                            {kpiVariation.transactions >= 0 ? "▲" : "▼"} {Math.abs(kpiVariation.transactions).toFixed(1)}% vs ant.
                                        </VariationBadge>
                                    )}
                                </TotalizerContent>
                            </TotalizerCard>

                            <TotalizerCard color="#27ae60">
                                <TotalizerIcon>
                                    <TrendingUp />
                                </TotalizerIcon>
                                <TotalizerContent>
                                    <TotalizerValue>
                                        {formatAbbrValue(totals.totalValue, true)}
                                    </TotalizerValue>
                                    <TotalizerLabel>Valor Total</TotalizerLabel>
                                    {!selectedMonth && kpiVariation.value !== null && (
                                        <VariationBadge positive={kpiVariation.value >= 0}>
                                            {kpiVariation.value >= 0 ? "▲" : "▼"} {Math.abs(kpiVariation.value).toFixed(1)}% vs ant.
                                        </VariationBadge>
                                    )}
                                </TotalizerContent>
                            </TotalizerCard>

                            <TotalizerCard color="#3498db">
                                <TotalizerIcon>
                                    <CalendarToday />
                                </TotalizerIcon>
                                <TotalizerContent>
                                    <TotalizerValue>{formatAbbrValue(totals.averagePerMonth)}</TotalizerValue>
                                    <TotalizerLabel>Média por Mês</TotalizerLabel>
                                </TotalizerContent>
                            </TotalizerCard>

                            <TotalizerCard color="#f39c12">
                                <TotalizerIcon>
                                    <Assessment />
                                </TotalizerIcon>
                                <TotalizerContent>
                                    <TotalizerValue>{formatAbbrValue(totals.provisioned)}</TotalizerValue>
                                    <TotalizerLabel>Contratado (Período)</TotalizerLabel>
                                </TotalizerContent>
                            </TotalizerCard>
                        </TotalizersGrid>

                        {metrics.length > 0 ? (
                            <>
                                <ChartsRow>
                                    {!selectedMonth && (
                                        <ChartCard>
                                            <Chart
                                                chartType="LineChart"
                                                width="100%"
                                                height="350px"
                                                data={prepareEvolutionChartData()}
                                                options={evolutionChartOptions}
                                            />
                                        </ChartCard>
                                    )}

                                    <ChartCard>
                                        <ChartHeader>
                                            <ChartTitle>Por Tipo</ChartTitle>
                                            <ToggleGroup>
                                                <ToggleBtn active={typeChartMode === 'count'} color="#667eea" onClick={() => setTypeChartMode('count')}>Processos</ToggleBtn>
                                                <ToggleBtn active={typeChartMode === 'value'} color="#667eea" onClick={() => setTypeChartMode('value')}>Valor</ToggleBtn>
                                            </ToggleGroup>
                                        </ChartHeader>
                                        <Chart
                                            chartType="PieChart"
                                            width="100%"
                                            height="320px"
                                            data={typeChartMode === 'count' ? prepareTypeChartData() : prepareTypeValueData()}
                                            options={{ ...typeChartOptions, title: typeChartMode === 'count' ? "Processos por Tipo" : "Valor por Tipo" }}
                                        />
                                    </ChartCard>
                                </ChartsRow>

                                {!selectedMonth && (
                                    <FullWidthChartCard>
                                        <Chart
                                            chartType="ColumnChart"
                                            width="100%"
                                            height="400px"
                                            data={prepareValueChartData()}
                                            options={valueChartOptions}
                                        />
                                    </FullWidthChartCard>
                                )}

                                {selectedApp === 'all' && (
                                    <FullWidthChartCard>
                                        <Chart
                                            chartType="ColumnChart"
                                            width="100%"
                                            height="400px"
                                            data={prepareAppChartData()}
                                            options={{
                                                title: `Processos por Aplicação${selectedYear ? ` - ${selectedYear}` : ''}`,
                                                chartArea: { width: "70%", height: "70%" },
                                                hAxis: {
                                                    title: "Aplicação",
                                                    titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
                                                },
                                                vAxis: {
                                                    title: "Quantidade de Processos",
                                                    titleTextStyle: { color: "#2c3e50", fontSize: 14, bold: true },
                                                    minValue: 0,
                                                },
                                                colors: ["#667eea"],
                                                legend: { position: "none" },
                                                animation: {
                                                    startup: true,
                                                    duration: 1000,
                                                    easing: "out",
                                                },
                                            }}
                                        />
                                    </FullWidthChartCard>
                                )}

                                <TableCard>
                                    <TableTitleRow>
                                        <TableTitle style={{ margin: 0 }}>Detalhamento Mensal{selectedYear ? ` - ${selectedYear}` : ''}</TableTitle>
                                        <ExportBtn onClick={exportCSV}>⬇ Exportar CSV</ExportBtn>
                                    </TableTitleRow>
                                    <TableWrapper>
                                        <DataTable>
                                            <thead>
                                                <tr>
                                                    {selectedApp === 'all' && <TableHeader>Aplicação</TableHeader>}
                                                    <TableHeader>Mês</TableHeader>
                                                    <TableHeader align="right">Compras</TableHeader>
                                                    <TableHeader align="right">Devoluções</TableHeader>
                                                    <TableHeader align="right">Adiantamentos</TableHeader>
                                                    <TableHeader align="right">Total Processos</TableHeader>
                                                    <TableHeader align="right">Valor Total</TableHeader>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedMetrics.map((metric, index) => {
                                                        const monthNames = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
                                                                           "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
                                                        const monthIndex = metric.month - 1;
                                                        const monthName = monthNames[monthIndex];

                                                        return (
                                                            <TableRow key={`${metric.appId}-${metric.year}-${metric.month}-${index}`}>
                                                                {selectedApp === 'all' && (
                                                                    <TableCell><strong>{metric.appName}</strong></TableCell>
                                                                )}
                                                                <TableCell><strong>{monthName}</strong></TableCell>
                                                                <TableCell align="right">
                                                                    <MetricBadge color="#667eea">
                                                                        {(metric.purchase?.processed || 0).toLocaleString('pt-BR')}
                                                                    </MetricBadge>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <MetricBadge color="#764ba2">
                                                                        {(metric.return?.processed || 0).toLocaleString('pt-BR')}
                                                                    </MetricBadge>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <MetricBadge color="#3498db">
                                                                        {(metric.downpayment?.processed || 0).toLocaleString('pt-BR')}
                                                                    </MetricBadge>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <MetricBadge color="#2c3e50">
                                                                        {metric.transactions.toLocaleString('pt-BR')}
                                                                    </MetricBadge>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <MetricBadge color="#27ae60">
                                                                        {metric.totalValue.toLocaleString('pt-BR', {
                                                                            style: 'currency',
                                                                            currency: 'BRL'
                                                                        })}
                                                                    </MetricBadge>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                            </tbody>
                                        </DataTable>
                                    </TableWrapper>
                                    <PaginationContainer>
                                        <PaginationInfo>
                                            Mostrando {Math.min(((currentPage - 1) * rowsPerPage) + 1, sortedMetrics.length)}-{Math.min(currentPage * rowsPerPage, sortedMetrics.length)} de {sortedMetrics.length} registros
                                        </PaginationInfo>
                                        <PaginationControls>
                                            <PaginationLabel>Linhas por página:</PaginationLabel>
                                            <PaginationSelect value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                            </PaginationSelect>
                                            <PaginationButton disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Anterior</PaginationButton>
                                            <PaginationPageInfo>Página {currentPage} de {totalPages}</PaginationPageInfo>
                                            <PaginationButton disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>Próxima</PaginationButton>
                                        </PaginationControls>
                                    </PaginationContainer>
                                </TableCard>
                            </>
                        ) : (
                            <EmptyStateCard>
                                <EmptyIcon>
                                    <Assessment style={{ fontSize: '64px', color: '#cbd5e1' }} />
                                </EmptyIcon>
                                <EmptyTitle>Nenhum dado encontrado</EmptyTitle>
                                <EmptyText>
                                    Não há métricas para <strong>{selectedAppName}</strong>{selectedYear ? ` em ${selectedYear}` : ''}
                                </EmptyText>
                            </EmptyStateCard>
                        )}
                    </>
                )}

                {!selectedApp && (
                    <EmptyStateContent>
                        <EmptyIcon>
                            <Assessment style={{ fontSize: '64px', color: '#cbd5e1' }} />
                        </EmptyIcon>
                        <EmptyTitle>Selecione uma aplicação</EmptyTitle>
                        <EmptyText>
                            Escolha uma aplicação e um ano para visualizar as métricas de processos
                        </EmptyText>
                    </EmptyStateContent>
                )}
            </ResultsCard>
        </Container>
    );
}

export default DashboardViceri;

// ===== STYLED COMPONENTS =====

const FiltersCard = styled.div`
    margin-bottom: 24px;
    padding-top: 24px;
`;

const FilterTitle = styled.h3`
    font-size: 18px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0 0 20px 0;
    font-family: 'Mulish', sans-serif;
`;

const FiltersContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
`;

const FilterGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const FilterLabel = styled.label`
    font-size: 14px;
    font-weight: 600;
    color: #4a5568;
    font-family: 'Mulish', sans-serif;
`;

const FilterSelect = styled.select`
    padding: 12px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    font-size: 14px;
    font-family: 'Mulish', sans-serif;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;

    &:focus {
        outline: none;
        border-color: #667eea;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }
`;

const TotalizersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 20px;
    margin-bottom: 24px;
`;

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const TotalizerCard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border: 1px solid #e8e8e8;
    display: flex;
    align-items: center;
    gap: 16px;
    animation: ${fadeIn} 0.5s ease;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
    }
`;

const TotalizerIcon = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: ${props => props.color || '#667eea'}15;
    color: ${props => props.color || '#667eea'};
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        font-size: 28px;
    }
`;

const TotalizerContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const TotalizerValue = styled.div`
    font-size: 24px;
    font-weight: 700;
    color: #2c3e50;
    font-family: 'Mulish', sans-serif;
`;

const TotalizerLabel = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    font-family: 'Mulish', sans-serif;
`;

const VariationBadge = styled.span`
    display: inline-block;
    margin-top: 6px;
    padding: 3px 9px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    font-family: 'Mulish', sans-serif;
    background: ${props => props.positive ? '#dcfce7' : '#fee2e2'};
    color: ${props => props.positive ? '#15803d' : '#dc2626'};
`;

const ChartHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
`;

const ChartTitle = styled.h4`
    font-size: 15px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0;
    font-family: 'Mulish', sans-serif;
`;

const ToggleGroup = styled.div`
    display: flex;
    gap: 4px;
`;

const ToggleBtn = styled.button`
    padding: 6px 14px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    border: 2px solid ${(props) => (props.active ? (props.color || '#667eea') : '#e5e7eb')};
    background: ${(props) => (props.active ? (props.color || '#667eea') : 'white')};
    color: ${(props) => (props.active ? 'white' : '#9ca3af')};
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: 'Mulish', sans-serif;
`;

const TableTitleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
`;

const ExportBtn = styled.button`
    padding: 8px 16px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    border: none;
    background: linear-gradient(135deg, #27ae60 0%, #219a52 100%);
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: 'Mulish', sans-serif;
    box-shadow: 0 2px 8px rgba(39, 174, 96, 0.3);

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(39, 174, 96, 0.4);
    }
`;

const ChartsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
    margin-bottom: 24px;

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
    }
`;

const ChartCard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border: 1px solid #e8e8e8;
    animation: ${fadeIn} 0.5s ease;
`;

const FullWidthChartCard = styled(ChartCard)`
    width: 100%;
    margin-bottom: 24px;
`;

const TableCard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border: 1px solid #e8e8e8;
    animation: ${fadeIn} 0.5s ease;
`;

const TableTitle = styled.h3`
    font-size: 18px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0 0 20px 0;
    font-family: 'Mulish', sans-serif;
`;

const TableWrapper = styled.div`
    overflow-x: auto;
`;

const DataTable = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const TableHeader = styled.th`
    text-align: ${props => props.align || 'left'};
    padding: 12px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #e5e7eb;
    font-family: 'Mulish', sans-serif;
`;

const TableRow = styled.tr`
    border-bottom: 1px solid #e5e7eb;
    transition: background 0.2s ease;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: #f8f9fa;
    }
`;

const TableCell = styled.td`
    text-align: ${props => props.align || 'left'};
    padding: 16px;
    font-size: 14px;
    color: #2c3e50;
    font-family: 'Mulish', sans-serif;
`;

const MetricBadge = styled.span`
    display: inline-block;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    background: ${props => props.color || '#667eea'}15;
    color: ${props => props.color || '#667eea'};
`;

const EmptyStateCard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 64px 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border: 1px solid #e8e8e8;
    text-align: center;
    animation: ${fadeIn} 0.5s ease;
`;

const EmptyStateContent = styled.div`
    text-align: center;
    padding: 64px 24px;
`;

const EmptyIcon = styled.div`
    margin-bottom: 24px;
`;

const EmptyTitle = styled.h3`
    font-size: 20px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0 0 12px 0;
    font-family: 'Mulish', sans-serif;
`;

const EmptyText = styled.p`
    font-size: 14px;
    color: #6b7280;
    margin: 0;
    font-family: 'Mulish', sans-serif;
`;

const PaginationContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0 0;
    margin-top: 16px;
    border-top: 1px solid #e5e7eb;
    flex-wrap: wrap;
    gap: 12px;
`;

const PaginationInfo = styled.span`
    font-size: 13px;
    color: #6b7280;
    font-family: 'Mulish', sans-serif;
`;

const PaginationControls = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const PaginationLabel = styled.span`
    font-size: 13px;
    color: #6b7280;
    font-family: 'Mulish', sans-serif;
`;

const PaginationSelect = styled.select`
    padding: 6px 10px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'Mulish', sans-serif;
    background: white;
    cursor: pointer;

    &:focus {
        outline: none;
        border-color: #667eea;
        box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
    }
`;

const PaginationButton = styled.button`
    padding: 8px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'Mulish', sans-serif;
    background: white;
    color: #2c3e50;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background: #f3f4f6;
        border-color: #667eea;
        color: #667eea;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const PaginationPageInfo = styled.span`
    font-size: 13px;
    color: #2c3e50;
    font-weight: 600;
    font-family: 'Mulish', sans-serif;
`;
